import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GripVertical, Settings } from 'lucide-react';
import type { Column } from '@tanstack/react-table';
import type { TableColumnDef } from './types.ts';

const COLUMN_MENU_WIDTH = 256;
const COLUMN_MENU_OFFSET = 8;

type ColumnMenuPosition = { top: number; left: number };

export interface ColumnVisibilityMenuProps<TData> {
  /** Hideable leaf columns to render as toggles. */
  columns: Column<TData, unknown>[];
  /** Reset all column visibility back to its default state. */
  onReset: () => void;
  ariaLabel?: string;
  /**
   * When true, columns can be drag-reordered in the menu (same pattern as Systems).
   * Visibility toggles stay unchanged. Opt-in so other tables are unaffected.
   */
  enableReorder?: boolean;
  /**
   * Called while dragging with the new order of the menu column ids.
   * Parent merges this into the full table columnOrder.
   */
  onReorder?: (orderedColumnIds: string[]) => void;
}

const DEFAULT_ARIA_LABEL = 'Show or hide columns';

function getColumnMenuLabel<TData>(column: Column<TData, unknown>): string {
  const def = column.columnDef as TableColumnDef<TData>;
  return def.columnLabel ?? column.id;
}

/**
 * Self-contained column visibility menu: renders a trigger button and a portaled
 * popup anchored beneath it. Owns its own open/position state and outside-click /
 * resize / scroll listeners so consumers only have to wire columns + a reset handler.
 */
export function ColumnVisibilityMenu<TData>({
  columns,
  onReset,
  ariaLabel = DEFAULT_ARIA_LABEL,
  enableReorder = false,
  onReorder,
}: ColumnVisibilityMenuProps<TData>): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<ColumnMenuPosition | null>(null);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const listScrollRef = useRef<HTMLDivElement>(null);
  const dragScrollDirectionRef = useRef<-1 | 0 | 1>(0);
  const dragScrollRafRef = useRef<number | null>(null);

  const stopDragAutoScroll = useCallback(() => {
    dragScrollDirectionRef.current = 0;
    if (dragScrollRafRef.current != null) {
      cancelAnimationFrame(dragScrollRafRef.current);
      dragScrollRafRef.current = null;
    }
  }, []);

  const ensureDragAutoScrollLoop = useCallback(() => {
    if (dragScrollRafRef.current != null) return;
    const tick = () => {
      const direction = dragScrollDirectionRef.current;
      const list = listScrollRef.current;
      if (!direction || !list) {
        dragScrollRafRef.current = null;
        return;
      }
      list.scrollTop += direction * 10;
      dragScrollRafRef.current = requestAnimationFrame(tick);
    };
    dragScrollRafRef.current = requestAnimationFrame(tick);
  }, []);

  const updateDragAutoScrollFromClientY = useCallback(
    (clientY: number) => {
      const list = listScrollRef.current;
      if (!list) return;
      const rect = list.getBoundingClientRect();
      const edge = 36;
      if (clientY < rect.top + edge) {
        dragScrollDirectionRef.current = -1;
        ensureDragAutoScrollLoop();
      } else if (clientY > rect.bottom - edge) {
        dragScrollDirectionRef.current = 1;
        ensureDragAutoScrollLoop();
      } else {
        stopDragAutoScroll();
      }
    },
    [ensureDragAutoScrollLoop, stopDragAutoScroll],
  );

  useEffect(() => () => stopDragAutoScroll(), [stopDragAutoScroll]);

  /**
   * Anchors the portaled menu under the trigger button. We clamp `left` to the viewport
   * so the menu stays visible even when the trigger sits flush against the right edge of
   * a wide table.
   */
  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const desiredLeft = rect.right - COLUMN_MENU_WIDTH;
    const maxLeft = Math.max(
      COLUMN_MENU_OFFSET,
      window.innerWidth - COLUMN_MENU_WIDTH - COLUMN_MENU_OFFSET,
    );
    const left = Math.min(Math.max(COLUMN_MENU_OFFSET, desiredLeft), maxLeft);
    setPosition({ top: rect.bottom + COLUMN_MENU_OFFSET, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const trigger = triggerRef.current;
      const popup = popupRef.current;
      const target = event.target as Node;
      if (trigger && trigger.contains(target)) return;
      if (popup && popup.contains(target)) return;
      stopDragAutoScroll();
      setOpen(false);
    };
    const onReposition = () => updatePosition();
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updatePosition, stopDragAutoScroll]);

  const handleReset = useCallback(() => {
    onReset();
    setOpen(false);
  }, [onReset]);

  const handleDragStart = useCallback(
    (key: string, canDrag: boolean) => (e: React.DragEvent) => {
      if (!enableReorder || !canDrag) {
        e.preventDefault();
        return;
      }
      setDragKey(key);
      e.dataTransfer.effectAllowed = 'move';
      try {
        e.dataTransfer.setData('text/plain', key);
      } catch (_) {
        /* ignore */
      }
    },
    [enableReorder],
  );

  const handleListDragOver = useCallback(
    (e: React.DragEvent) => {
      if (!enableReorder || !dragKey) return;
      e.preventDefault();
      updateDragAutoScrollFromClientY(e.clientY);
    },
    [dragKey, enableReorder, updateDragAutoScrollFromClientY],
  );

  const handleDragOver = useCallback(
    (overKey: string, overCanDrop: boolean) => (e: React.DragEvent) => {
      if (!enableReorder || !onReorder || !overCanDrop) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      updateDragAutoScrollFromClientY(e.clientY);
      if (!dragKey || dragKey === overKey) return;

      // Only reorder among currently checked (visible) columns — hidden stay put.
      const visibleIds = columns.filter((col) => col.getIsVisible()).map((col) => col.id);
      if (!visibleIds.includes(dragKey) || !visibleIds.includes(overKey)) return;

      const nextVisible = [...visibleIds];
      const fromIdx = nextVisible.indexOf(dragKey);
      const toIdx = nextVisible.indexOf(overKey);
      if (fromIdx < 0 || toIdx < 0) return;
      nextVisible.splice(fromIdx, 1);
      nextVisible.splice(toIdx, 0, dragKey);

      let visibleCursor = 0;
      const nextFull = columns.map((col) => {
        if (!col.getIsVisible()) return col.id;
        const nextId = nextVisible[visibleCursor];
        visibleCursor += 1;
        return nextId ?? col.id;
      });
      onReorder(nextFull);
    },
    [columns, dragKey, enableReorder, onReorder, updateDragAutoScrollFromClientY],
  );

  const handleDragEnd = useCallback(() => {
    stopDragAutoScroll();
    setDragKey(null);
  }, [stopDragAutoScroll]);

  const popup =
    open && position
      ? (createPortal(
          <div
            ref={popupRef}
            data-column-visibility-menu
            className="fixed w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
            style={{ top: position.top, left: position.left }}
          >
            <div className="px-3 py-2 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-900">Show columns</p>
              <p className="text-xs text-gray-500">
                {enableReorder
                  ? 'Select columns to display, or drag checked columns to reorder.'
                  : 'Select columns to display in this table.'}
              </p>
            </div>
            <div
              ref={listScrollRef}
              className="p-3 space-y-1 max-h-72 overflow-auto"
              onDragOver={handleListDragOver}
            >
              {columns.map((col) => {
                const isVisible = col.getIsVisible();
                const canDrag = enableReorder && isVisible;
                const isDragging = dragKey === col.id;
                return (
                  <label
                    key={col.id}
                    draggable={canDrag}
                    onDragStart={handleDragStart(col.id, canDrag)}
                    onDragOver={handleDragOver(col.id, canDrag)}
                    onDragEnd={handleDragEnd}
                    onDrop={(e) => e.preventDefault()}
                    className={`flex items-center gap-2 text-sm text-gray-700 py-0.5 ${
                      canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
                    } ${isDragging ? 'opacity-50 bg-blue-50 rounded' : ''} ${
                      !isVisible ? 'text-gray-400' : ''
                    }`}
                  >
                    {enableReorder ? (
                      <GripVertical
                        className={`h-3.5 w-3.5 shrink-0 ${canDrag ? 'text-gray-400' : 'text-gray-200'}`}
                        aria-hidden
                      />
                    ) : null}
                    <input
                      type="checkbox"
                      checked={isVisible}
                      onChange={() => col.toggleVisibility()}
                      className="h-4 w-4 cursor-pointer rounded border border-gray-300 text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    />
                    <span className="flex-1 truncate">{getColumnMenuLabel(col)}</span>
                  </label>
                );
              })}
            </div>
            <div className="px-3 py-2 border-t border-gray-100 flex justify-end">
              <button
                type="button"
                onClick={handleReset}
                className="text-xs font-medium text-blue-700 hover:text-blue-900"
              >
                Reset to default
              </button>
            </div>
          </div>,
          document.body,
        ) as unknown as React.ReactNode)
      : null;

  return (
    <>
      <div className="shrink-0" data-column-visibility-menu>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-label={ariaLabel}
          aria-expanded={open}
          className="inline-flex items-center justify-center h-9 w-9 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <Settings className="h-4 w-4" aria-hidden />
        </button>
      </div>
      {popup}
    </>
  );
}

export default ColumnVisibilityMenu;
